import { ensureOrgShifts } from "../../../../../../../../services/useOrgData";
import React, { useEffect, useMemo, useState } from 'react'
import { Avatar, Box, Stack } from '@mui/material'
import { useParams, useNavigate } from 'react-router-dom'
import BtnPrimary from '../../../../../../../../component/PrimaryButton';
import BtnSecondary from '../../../../../../../../component/SecondaryButton';
import FormField from '../../../../../../../../component/Formfield';
import SectionLoader from '../../../../../../../../component/SectionLoader';
import EmptyState from '../../../../../../../../component/EmptyState';
import { useNotify } from '../../../../../../../../component/NotificationProvider';
import { fetchEmployeesByIdArray } from '../../../../../../../../services/employee';

import { getAttendanceRequestById, reviewAttendanceRequest } from '../../../../../../../../services/attendanceRequest';
import { calculateTime } from '../../../../../../../../utils/calculateTime';
import { StatusChip } from '../../../../../../../../component/listing';
import {
  isAttested, sourceLabel, originTone, raisedByName, reviewerName, isSelfApproved, bypassSummary,
} from '../../../../../../../../utils/attendanceRequestOrigin';
import { tokens } from '../../../../../../../../theme';

import { displayFont, radius } from "../../../../../../../../theme";
const STATUS_TONE = { Pending: "warning", Approved: "success", Rejected: "danger" };
const TYPE_LABEL = { REGULARIZATION: "Regularization", SHIFT_CHANGE: "Shift Change" };
const initialsOf = (n) => (n || "?").split(" ").filter(Boolean).map((p) => p[0]).join("").slice(0, 2).toUpperCase();

const Field = ({ label, children }) => (
  <Box>
    <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, mb: "0.5rem", textTransform: "uppercase", letterSpacing: "0.03em" }}>{label}</Box>
    <Box sx={{ fontSize: "0.9375rem", fontWeight: 600, color: tokens.text }}>{children ?? "--"}</Box>
  </Box>
);

const RequestsDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const notify = useNotify();

  const [request, setRequest] = useState(null);
  const [employee, setEmployee] = useState(null);
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewerComment, setReviewerComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const shiftNameById = useMemo(() => {
    const m = {};
    (shifts || []).forEach((s) => { m[s._id] = s.name; });
    return m;
  }, [shifts]);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const res = await getAttendanceRequestById(id);
        if (res?.success === false) throw new Error(res?.message || "Failed to load request");
        const req = res?.data || null;
        if (!active) return;
        setRequest(req);
        // resolve employee + shifts fresh (avoids stale-cache "Unknown" / "--")
        const [empRes, shiftRes] = await Promise.all([
          req?.employeeId ? fetchEmployeesByIdArray([req.employeeId]) : Promise.resolve(null),
          req?.type === "SHIFT_CHANGE" ? ensureOrgShifts() : Promise.resolve(null),
        ]);
        if (!active) return;
        setEmployee(empRes?.data?.[0] || null);
        setShifts(Array.isArray(shiftRes) ? shiftRes : []);
      } catch (error) {
        if (active) notify.error(error?.message || "Failed to load request");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleReview = async (status) => {
    if (!reviewerComment.trim()) { notify.error("Please add a review comment"); return; }
    setSubmitting(true);
    try {
      const res = await reviewAttendanceRequest(id, { status, reviewerComment: reviewerComment.trim() });
      if (res?.success === false) throw new Error(res?.message || "Failed to review request");
      notify.success(`Request ${status.toLowerCase()}`);
      navigate("/app/attendance-details/requests");
    } catch (error) {
      notify.error(error?.message || "Failed to review request");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <SectionLoader minHeight="40vh" />;
  if (!request) return <EmptyState title="Request not found" />;

  const name = employee ? `${employee.fname || ""} ${employee.lname || ""}`.trim() : "Unknown";
  const role = employee ? [employee.designation, employee.employmentType].filter(Boolean).join(" · ") : "";
  const isPending = request.status === "Pending";
  const cardSx = { background: tokens.card, border: `1px solid ${tokens.border}`, borderRadius: `${radius.card}px`, p: "1.5rem", mb: "1.25rem" };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: "1.25rem" }}>
        <Box sx={{ fontFamily: displayFont, fontSize: "1.25rem", fontWeight: 600, color: tokens.text }}>Request details</Box>
        <BtnSecondary to="/app/attendance-details/requests" sx={{ borderRadius: "8px" }}>Back</BtnSecondary>
      </Stack>

      {/* employee + summary header */}
      <Box sx={cardSx}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1.5}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Avatar src={employee?.profileImage || undefined} sx={{ width: "3rem", height: "3rem", bgcolor: tokens.accent, fontWeight: 600 }}>{initialsOf(name)}</Avatar>
            <Box>
              <Box sx={{ fontSize: "1rem", fontWeight: 600, color: tokens.text }}>{name}</Box>
              <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>{[employee?.aliasId, role].filter(Boolean).join(" · ") || "—"}</Box>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <StatusChip label={TYPE_LABEL[request.type] || request.type} tone="info" />
            <StatusChip label={sourceLabel(request)} tone={originTone(request)} />
            <StatusChip label={request.status} tone={STATUS_TONE[request.status]} />
          </Stack>
        </Stack>
      </Box>

      {/* An attestation is an assertion made about this employee by someone with
          the power to change their pay, and it was approved by its own author.
          That is correct — routing it back to the same person would be theatre —
          but it must be stated, not left for the reader to deduce from two
          matching ids. */}
      {isAttested(request) && (
        <Box
          sx={{
            ...cardSx,
            borderColor: `${tokens.accent}59`,
            background: `${tokens.accentTint}`,
          }}
        >
          <Box sx={{ fontSize: "0.875rem", fontWeight: 600, color: tokens.text, mb: "0.5rem" }}>
            Corrected on the employee's behalf
          </Box>
          <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, lineHeight: 1.5 }}>
            {raisedByName(request) || "A supervisor"} recorded this correction
            {isSelfApproved(request)
              ? " and approved it themselves — it was not separately reviewed."
              : reviewerName(request)
              ? ` and ${reviewerName(request)} approved it.`
              : "."}
            {" "}The employee did not file it, so their agreement with it is not on record here.
            {bypassSummary(request) && (
              <>
                {" "}This correction was accepted{" "}
                <Box component="span" sx={{ color: tokens.warningText, fontWeight: 600 }}>
                  {bypassSummary(request)}
                </Box>
                , which a self-service request would have been refused for.
              </>
            )}
          </Box>
        </Box>
      )}

      {/* request specifics */}
      <Box sx={cardSx}>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" }, gap: "1.5rem" }}>
          {request.type === "REGULARIZATION" && (
            <>
              <Field label="Date">{request.dayString}</Field>
              <Field label="Requested Clock In">{request.updatedClockInTime != null ? calculateTime(request.updatedClockInTime) : "--"}</Field>
              <Field label="Requested Clock Out">{request.updatedClockOutTime != null ? calculateTime(request.updatedClockOutTime) : "--"}</Field>
            </>
          )}
          {request.type === "SHIFT_CHANGE" && (
            <>
              <Field label="Current Shift">{shiftNameById[request.currentShift] || "Not set"}</Field>
              <Field label="Requested Shift">{shiftNameById[request.requestedShift] || "Not set"}</Field>
              <Field label="Requested Location">{request.requestedLocation || "--"}</Field>
            </>
          )}
        </Box>
        <Box sx={{ mt: "1.5rem", pt: "1.25rem", borderTop: `1px solid ${tokens.border}` }}>
          <Field label="Reason"><Box sx={{ fontWeight: 400 }}>{request.reason}</Box></Field>
        </Box>
      </Box>

      {/* review */}
      <Box sx={cardSx}>
        {isPending ? (
          <>
            <Box sx={{ fontSize: "0.8125rem", fontWeight: 600, color: tokens.text, mb: "0.5rem" }}>Review comment</Box>
            <FormField type="text" value={reviewerComment} onChange={(e) => setReviewerComment(e.target.value)} fullwidth row={3} />
            <Stack direction="row" justifyContent="flex-end" spacing={1.5} sx={{ mt: "1.25rem" }}>
              <BtnSecondary onClick={() => handleReview("Rejected")} disabled={submitting}
                sx={{ height: "3rem", px: "2rem", borderRadius: "10px", color: tokens.danger, borderColor: tokens.dangerBorder }}>
                Reject
              </BtnSecondary>
              <BtnPrimary onClick={() => handleReview("Approved")} disabled={submitting} sx={{ borderRadius: "8px" }}>
                Approve
              </BtnPrimary>
            </Stack>
          </>
        ) : (
          <Field label="Reviewer comment"><Box sx={{ fontWeight: 400 }}>{request.reviewerComment || "--"}</Box></Field>
        )}
      </Box>
    </Box>
  )
}

export default RequestsDetails
